import type { Procrastination, Detour, WisdomUpgrade } from './types';

export const PROCRASTINATIONS: Procrastination[] = [
  {
    id: 'daydreaming',
    name: 'Daydreaming',
    description: 'Stare blankly out the window and ponder what to eat for lunch.',
    baseCost: 15,
    baseRate: 0.1,
    costExponent: 1.15
  },
  {
    id: 'checking_fridge',
    name: 'Checking the Fridge',
    description: 'Open the fridge, stare at the mustard, close it. Repeat in 5 minutes.',
    baseCost: 100,
    baseRate: 1.0,
    costExponent: 1.16
  },
  {
    id: 'doomscrolling',
    name: 'Social Doomscrolling',
    description: 'Infinite swipe down on social feeds. Brain cells are actively leaving your skull.',
    baseCost: 1100,
    baseRate: 8.0,
    costExponent: 1.17
  },
  {
    id: 'wiki_rabbit_hole',
    name: 'Wiki Rabbit Hole',
    description: 'Started researching quantum mechanics, now reading the history of the spork.',
    baseCost: 12000,
    baseRate: 47.0,
    costExponent: 1.18
  },
  {
    id: 'desktop_organizing',
    name: 'Desktop Organizing',
    description: 'Sort your 200 cluttered files by color, size, and date modified. Then group them by mood.',
    baseCost: 130000,
    baseRate: 260.0,
    costExponent: 1.19
  },
  {
    id: 'pointless_meetings',
    name: 'Avoidable Meetings',
    description: 'Attending status updates that could have been a single word email.',
    baseCost: 1400000,
    baseRate: 1400.0,
    costExponent: 1.20
  },
  {
    id: 'watching_grass',
    name: 'Watching Grass Grow',
    description: 'Actually go outside (terrifying) and watch blades of grass slowly expand.',
    baseCost: 15000000,
    baseRate: 7800.0,
    costExponent: 1.21
  },
  {
    id: 'dmv_line',
    name: 'DMV Waiting Simulator',
    description: 'Enter a waiting room where time-space itself dilates and slows to a crawl.',
    baseCost: 170000000,
    baseRate: 44000.0,
    costExponent: 1.22
  },
  {
    id: 'loading_bar',
    name: 'Staring at a Progress Bar',
    description: 'Watch a download status bar stuck at 99%. Any millisecond now...',
    baseCost: 2000000000,
    baseRate: 260000.0,
    costExponent: 1.23
  },
  {
    id: 'npm_install',
    name: 'Waiting for NPM Install',
    description: 'The black hole of development. Downloading the weight of the universe in node_modules.',
    baseCost: 25000000000,
    baseRate: 1600000.0,
    costExponent: 1.25
  }
];

export const DETOURS: Detour[] = [
  {
    id: 'deep_sigh',
    name: 'Heavy Sigh',
    description: 'Exhale dramatically to show the world how hard it is to do nothing.',
    baseCost: 10,
    baseIncrement: 1.0,
    costExponent: 1.12
  },
  {
    id: 'fidget_spin',
    name: 'Fidget Toy Spin',
    description: 'Spin a tiny plastic bearing until your index finger gets tired.',
    baseCost: 150,
    baseIncrement: 5.0,
    costExponent: 1.13
  },
  {
    id: 'pencil_tapping',
    name: 'Pencil Tapping',
    description: 'Create an erratic drum beat on your desk. Drives colleagues insane.',
    baseCost: 1800,
    baseIncrement: 25.0,
    costExponent: 1.14
  },
  {
    id: 'keyboard_clack',
    name: 'Mechanical Key Clack',
    description: 'Mash random keys on heavy blue switches to sound incredibly productive.',
    baseCost: 22000,
    baseIncrement: 150.0,
    costExponent: 1.15
  },
  {
    id: 'slack_screen',
    name: 'Dual Screen Slack / Cat Videos',
    description: 'Set up an extra monitor dedicated solely to Slack alerts and animal compilations.',
    baseCost: 300000,
    baseIncrement: 1000.0,
    costExponent: 1.16
  }
];

export const WISDOM_UPGRADES: WisdomUpgrade[] = [
  {
    id: 'time_dilation',
    name: 'Time Dilation',
    description: 'Unlocks deeper levels of slow-motion thinking. +10% passive time wasted per level.',
    baseCost: 5,
    costExponent: 1.5,
    maxLevel: 15,
    effectType: 'passive_boost'
  },
  {
    id: 'subconscious_efficiency',
    name: 'Subconscious Idle',
    description: 'Your passive clicks waste more time. +15% click detour efficiency per level.',
    baseCost: 8,
    costExponent: 1.6,
    maxLevel: 15,
    effectType: 'click_boost'
  },
  {
    id: 'idle_stupor',
    name: 'Lethargic Bliss',
    description: 'You feel no rush. Reduces the cost scaling of all Procrastinations by 2% per level.',
    baseCost: 10,
    costExponent: 1.7,
    maxLevel: 5,
    effectType: 'cost_reduction'
  },
  {
    id: 'auto_sigher',
    name: 'Subconscious Sighing',
    description: 'You sigh even when asleep. Automatically clicks 1 time per second per level.',
    baseCost: 15,
    costExponent: 2.0,
    maxLevel: 5,
    effectType: 'auto_click'
  }
];

export function getProcrastinationCost(proc: Procrastination, count: number, costReductionLevel: number = 0): number {
  const reduction = 1 - (costReductionLevel * 0.02); // 2% reduction per level
  const baseCost = proc.baseCost * reduction;
  return Math.floor(baseCost * Math.pow(proc.costExponent, count));
}

export function getDetourCost(detour: Detour, count: number): number {
  return Math.floor(detour.baseCost * Math.pow(detour.costExponent, count));
}

export function getWisdomUpgradeCost(wu: WisdomUpgrade, level: number): number {
  return Math.floor(wu.baseCost * Math.pow(wu.costExponent, level));
}
